/**
 * Mobile results sheet physics (#856) — pure functions ported from the
 * toolkit's explorer-mobile-sheet.js v2 (homerunner-toolkit origin/main,
 * assets/js/explorer-mobile-sheet.js). No DOM in here so every rule is unit
 * testable; MobileResultsLayout.tsx owns the elements and the listeners.
 *
 * Snap model (js:1278-1289, `snaps()`): three resting positions expressed as
 * a translateY in px from the top of the visual viewport — `full` (sheet
 * nearly covers the map, list mode), `half` (the approved prototype's
 * resting point) and `peek` (map-first, handle + count visible). Heights
 * come from visualViewport so an iOS URL-bar collapse or the keyboard does
 * not drift them.
 */

export type MobileResultsSnap = 'peek' | 'half' | 'full'

export type SnapPx = Record<MobileResultsSnap, number>

export type FramePadding = { top: number; right: number; bottom: number; left: number }

/** Toolkit js:1284-1287 — fraction of the visual viewport height per snap. */
export const SNAP_FRACTIONS: Readonly<Record<MobileResultsSnap, number>> = {
  full: 0.06,
  half: 0.52,
  peek: 0.86,
}

/** Snaps ordered top → bottom (ascending translateY). */
export const SNAP_ORDER: readonly MobileResultsSnap[] = ['full', 'half', 'peek']

/**
 * Drag overshoot beyond the outer snaps (js:1350): the sheet may be pulled
 * 10px above `full` and 40px below `peek` before the clamp bites, so a drag
 * feels attached rather than hitting a wall exactly at the snap.
 */
export const DRAG_OVERSHOOT_TOP_PX = 10
export const DRAG_OVERSHOOT_BOTTOM_PX = 40

/**
 * Inertial settle (widget-side addition the issue asks for; the module only
 * snaps to the nearest point). A release faster than this, in px per ms,
 * advances ONE snap in the flick direction instead of settling nearest — a
 * quick downward flick from `full` lands on `half`, not back on `full`.
 */
export const FLICK_VELOCITY_PX_PER_MS = 0.6

export function computeSnaps(viewportHeight: number): SnapPx {
  const h = Math.max(0, viewportHeight)
  return {
    full: Math.round(h * SNAP_FRACTIONS.full),
    half: Math.round(h * SNAP_FRACTIONS.half),
    peek: Math.round(h * SNAP_FRACTIONS.peek),
  }
}

/** js:1350 — the live drag position clamp. */
export function clampDragTranslate(translate: number, snaps: SnapPx): number {
  return Math.min(snaps.peek + DRAG_OVERSHOOT_BOTTOM_PX, Math.max(snaps.full - DRAG_OVERSHOOT_TOP_PX, translate))
}

/**
 * js:1373-1381 — nearest snap to a translateY. Ties resolve to the EARLIEST
 * candidate in `full, half, peek` order (strict `<` against an Infinity seed),
 * which is what the tests pin; the `half` seed is only the empty-loop fallback.
 * Reordering the loop changes tie behaviour.
 */
export function nearestSnap(translate: number, snaps: SnapPx): MobileResultsSnap {
  let best: MobileResultsSnap = 'half'
  let bestDistance = Infinity
  for (const name of ['full', 'half', 'peek'] as const) {
    const d = Math.abs(translate - snaps[name])
    if (d < bestDistance) {
      bestDistance = d
      best = name
    }
  }
  return best
}

/**
 * Where a released drag settles. Slow release = nearest snap (module rule).
 * Fast release = the next snap in the flick direction, measured from the
 * release position, so a flick can never skip a snap or reverse.
 */
export function settleSnap(translate: number, snaps: SnapPx, velocityPxPerMs = 0): MobileResultsSnap {
  if (velocityPxPerMs >= FLICK_VELOCITY_PX_PER_MS) {
    // Downward flick: first snap at or below the release point.
    for (const name of SNAP_ORDER) if (snaps[name] >= translate) return name
    return 'peek'
  }
  if (velocityPxPerMs <= -FLICK_VELOCITY_PX_PER_MS) {
    // Upward flick: last snap at or above the release point.
    for (let i = SNAP_ORDER.length - 1; i >= 0; i--) {
      const name = SNAP_ORDER[i]
      if (snaps[name] <= translate) return name
    }
    return 'full'
  }
  return nearestSnap(translate, snaps)
}

/** js:1313-1316 — read the current translateY back off an inline transform. */
export function parseTranslateY(transform: string | null | undefined): number | null {
  if (!transform) return null
  const m = /translateY\(\s*(-?[0-9.]+)px\s*\)/.exec(transform)
  return m ? parseFloat(m[1]) : null
}

export interface FramePaddingInput {
  /** The map element's viewport rect. */
  mapRect: { top: number; left: number; right: number; bottom: number; width: number; height: number }
  /** visualViewport geometry (offsetLeft/offsetTop/width/height), or the layout viewport when absent. */
  viewport: { left: number; top: number; width: number; height: number }
  /** Viewport Y of the sheet's top edge at the snap the frame targets. */
  sheetTop: number
  /** Viewport Y of the fixed top bar's bottom edge, or null when there is no bar. */
  topbarBottom: number | null
}

/** js:686-707 — keep at least this much fit area so a provider never over-zooms. */
export const MIN_FIT_HEIGHT_PX = 120
export const MIN_FIT_WIDTH_PX = 60
/** js:674 — gap under the top bar so a pin never frames behind the pill. */
export const TOPBAR_GAP_PX = 8

/**
 * js:669-707 (`visibleTopOfMap` + `bandPadding`): padding, relative to the
 * map element, that insets a fitBounds into the VISIBLE band — below the
 * top bar, above the sheet, inside the visual viewport horizontally.
 * Clamped so the remaining fit area never collapses.
 */
export function computeFramePadding(input: FramePaddingInput): FramePadding {
  const { mapRect: r, viewport: vv, sheetTop, topbarBottom } = input
  const floor = Math.max(0, Math.round(Math.max(r.top, vv.top)))
  const visTop = topbarBottom !== null
    ? Math.max(floor, Math.round(topbarBottom) + TOPBAR_GAP_PX)
    : floor + 2
  const pad: FramePadding = {
    top: Math.max(0, Math.round(visTop - r.top)),
    bottom: Math.max(0, Math.round(r.bottom - sheetTop)),
    left: Math.max(0, Math.round(vv.left - r.left)),
    right: Math.max(0, Math.round(r.right - (vv.left + vv.width))),
  }
  if (pad.top + pad.bottom > r.height - MIN_FIT_HEIGHT_PX) {
    let over = pad.top + pad.bottom - (r.height - MIN_FIT_HEIGHT_PX)
    // Take it out of the bottom first (the sheet can be looked past; the topbar
    // cannot), then out of the top. Shrinking only the bottom silently broke
    // the documented floor whenever pad.top ALONE exceeded the budget — a large
    // navOffsetPx on a short viewport handed the provider a fit box under
    // 120px and fitBounds computed its zoom against an almost-collapsed area.
    const fromBottom = Math.min(pad.bottom, over)
    pad.bottom -= fromBottom
    over -= fromBottom
    if (over > 0) pad.top = Math.max(0, pad.top - over)
  }
  if (pad.left + pad.right > r.width - MIN_FIT_WIDTH_PX) {
    pad.left = 0
    pad.right = 0
  }
  return pad
}

/**
 * The snap a frame targets: the sheet's current snap, except in list mode
 * (`full`) where the map is covered — frame for `half` instead so the map
 * is sensibly framed the moment the visitor flips back to it.
 */
export function frameSnapFor(snap: MobileResultsSnap): Exclude<MobileResultsSnap, 'full'> {
  return snap === 'full' ? 'half' : snap
}
